import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { VehiclesService } from '../../services/vehicles.service';
import { StatusService } from '../../services/status.service';
import { Vehicle } from '../../models/Vehicle';
import { Status } from '../../models/Status';
import { TableAction } from '../../shared/components/table/table.component';
import { VEHICLE_BRANDS_FILTER_OPTIONS } from '../../shared/constants/vehicle-brands';
import { VEHICLE_EXPORT_COLUMN_LABELS } from '../../shared/constants/vehicle-export-columns';
import { ErrorService } from '../../shared/services/error.service';

@Component({
  selector: 'app-vehicles',
  templateUrl: './vehicles.component.html',
  styleUrls: ['./vehicles.component.css']
})
export class VehiclesComponent implements OnInit, OnDestroy {
  vehicles: Vehicle[] = [];
  filteredVehicles: Vehicle[] = [];
  isLoading = false;
  loadingStatuses = false;
  page = 1;
  pageSize = 10;
  totalItems = 0;
  totalPages = 0;
  hasNextPage = false;
  hasPreviousPage = false;
  
  filters = {
    status: '',
    brand: '',
    ownershipType: '',
    search: ''
  };
  
  /** Ordenação enviada à API: sortBy (nome do campo) e sortOrder (asc | desc) */
  sortBy: string | null = null;
  sortOrder: 'asc' | 'desc' = 'asc';

  showExportModal = false;
  loadingExportColumns = false;
  isExporting = false;
  exportError: string | null = null;
  availableExportColumns: string[] = [];
  selectedExportColumns: string[] = [];
  
  private searchTimeout: any;

  statusOptions: { value: string; label: string }[] = [];
  brandOptions: { value: string; label: string }[] = [];
  ownershipOptions: { value: string; label: string }[] = [];

  tableTitle = 'Veículos';
  showSearch = true;
  searchPlaceholder = 'Buscar por placa, chassi, modelo ou marca...';
  
  actions: TableAction[] = [
    {
      label: 'Visualizar',
      icon: 'fas fa-eye',
      action: 'view',
      class: 'btn-view'
    },
    {
      label: 'IPVA / Licenciamento',
      icon: 'fas fa-file-invoice-dollar',
      action: 'ipvaLicensing',
      class: 'btn-view'
    },
    {
      label: 'Editar',
      icon: 'fas fa-edit',
      action: 'edit',
      class: 'btn-edit'
    },
    {
      label: 'Excluir',
      icon: 'fas fa-trash',
      action: 'delete',
      class: 'btn-delete'
    }
  ];

  constructor(
    private router: Router, 
    private vehiclesService: VehiclesService,
    private statusService: StatusService,
    private errorService: ErrorService
  ) {}

  ngOnInit(): void {
    this.buildInitialFilterOptions();
    this.loadVehicleStatuses();
    this.loadVehicles();
  }

  loadVehicles(): void {
    this.isLoading = true;
    
    const backendFilters: { status?: string; brand?: string; ownershipType?: string; search?: string } = {};
    
    if (this.filters.status) backendFilters.status = this.filters.status;
    if (this.filters.brand) backendFilters.brand = this.filters.brand;
    if (this.filters.ownershipType) backendFilters.ownershipType = this.filters.ownershipType;
    if (this.filters.search?.trim()) backendFilters.search = this.filters.search.trim();
    
    const hasFilters = Object.keys(backendFilters).length > 0;
    
    const sortParam = this.sortBy ? { sortBy: this.sortBy, sortOrder: this.sortOrder } : undefined;
    
    this.vehiclesService.getAllPaginated(
      this.page, 
      this.pageSize, 
      hasFilters ? backendFilters : undefined,
      sortParam
    ).subscribe({
      next: res => {
        this.vehicles = res.items;
        this.filteredVehicles = res.items;
        this.page = res.page;
        this.pageSize = res.pageSize;
        this.totalItems = res.totalItems;
        this.totalPages = res.totalPages;
        this.hasNextPage = this.page < this.totalPages;
        this.hasPreviousPage = this.page > 1;
        this.isLoading = false;
      },
      error: () => { 
        this.isLoading = false; 
      }
    });
  }

  buildInitialFilterOptions(): void {
    this.ownershipOptions = [
      { value: '', label: 'Todos os Tipos' },
      { value: 'Próprio', label: 'Próprio' },
      { value: 'Locado', label: 'Locado' },
      { value: 'Financiado', label: 'Financiado' }
    ];
    
    this.brandOptions = VEHICLE_BRANDS_FILTER_OPTIONS;
  }

  loadVehicleStatuses(): void {
    this.loadingStatuses = true;
    this.statusService.getByEntity('Vehicle', true)
      .subscribe({
        next: (statuses: Status[]) => {
          this.statusOptions = [
            { value: '', label: 'Todos os Status' },
            ...statuses.map(status => ({
              value: status.name,
              label: status.name
            }))
          ];
          this.loadingStatuses = false;
        },
        error: () => {
          this.loadingStatuses = false;
          this.statusOptions = [
            { value: '', label: 'Todos os Status' },
            { value: 'Ativo', label: 'Ativo' },
            { value: 'Inativo', label: 'Inativo' }
          ];
        }
      });
  }

  applyFilters(): void {
    this.page = 1;
    this.loadVehicles();
  }

  clearFilters(): void {
    this.filters = { status: '', brand: '', ownershipType: '', search: '' };
    this.page = 1;
    this.loadVehicles();
  }

  onSearchChange(searchTerm: string): void {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
    
    this.filters.search = searchTerm;
    this.searchTimeout = setTimeout(() => {
      this.page = 1;
      this.loadVehicles();
      this.searchTimeout = null;
    }, 500);
  }
  
  ngOnDestroy(): void {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
  }

  view(vehicle: Vehicle): void {
    this.router.navigate(['/fleet/vehicles', vehicle.id, 'view']);
  }

  edit(vehicle: Vehicle): void { 
    this.router.navigate(['/fleet/vehicles', vehicle.id, 'edit']); 
  }

  newVehicle(): void { 
    this.router.navigate(['/fleet/vehicles/new']); 
  }

  remove(id: number): void { 
    this.vehiclesService.delete(id).subscribe(() => this.loadVehicles()); 
  }

  onActionClick(actionData: { action: string; item: any }): void {
    const { action, item } = actionData;
    const vehicle = item as Vehicle;
    
    if (action === 'view') {
      this.view(vehicle);
    } else if (action === 'ipvaLicensing') {
      this.router.navigate(['/financial/ipva-licensing'], { queryParams: { vehicleId: vehicle.id } });
    } else if (action === 'edit') {
      this.edit(vehicle);
    } else if (action === 'delete') {
      if (confirm(`Tem certeza que deseja excluir o veículo "${vehicle?.plateNumber || vehicle?.id}"?`)) {
        this.remove(vehicle.id);
      }
    }
  }

  nextPage(): void {
    if (this.hasNextPage) {
      this.page++;
      this.loadVehicles();
    }
  }

  prevPage(): void {
    if (this.hasPreviousPage) {
      this.page--;
      this.loadVehicles();
    }
  }

  onPageChange(page: number): void {
    this.page = page;
    this.loadVehicles();
  }

  onPageSizeChange(size: number): void {
    this.pageSize = size;  
    this.page = 1;
    this.loadVehicles();
  }

  /** Ordenação: API espera /api/vehicles?sortBy=plateNumber&sortOrder=asc (camelCase). */
  onSortChange(event: { sortBy: string; sortOrder: 'asc' | 'desc' }): void {
    this.sortBy = this.normalizeSortByForApi(event.sortBy);
    this.sortOrder = event.sortOrder === 'asc' || event.sortOrder === 'desc' ? event.sortOrder : 'asc';
    this.page = 1;
    this.loadVehicles();
  }

  private normalizeSortByForApi(columnLabel: string): string {
    const apiSortFields: Record<string, string> = {
      plateNumber: 'plateNumber', PlateNumber: 'plateNumber',
      chassisNumber: 'chassisNumber', ChassisNumber: 'chassisNumber',
      brand: 'brand', Brand: 'brand',
      model: 'model', Model: 'model',
      ownershipType: 'ownershipType', OwnershipType: 'ownershipType',
      status: 'status', Status: 'status'
    };
    return apiSortFields[columnLabel] ?? columnLabel;
  }

  openExportModal(): void {
    this.showExportModal = true;
    this.exportError = null;
    this.selectedExportColumns = [];
    this.loadingExportColumns = true;
    this.vehiclesService.getVehicleExportColumns().subscribe({
      next: (res) => {
        this.availableExportColumns = res?.columns || [];
        this.loadingExportColumns = false;
      },
      error: () => {
        this.availableExportColumns = [];
        this.loadingExportColumns = false;
      }
    });
  }

  closeExportModal(): void {
    this.showExportModal = false;
    this.exportError = null;
    this.availableExportColumns = [];
    this.selectedExportColumns = [];
  }

  getExportColumnLabel(columnKey: string): string {
    return VEHICLE_EXPORT_COLUMN_LABELS[columnKey] ?? columnKey;
  }

  toggleExportColumn(columnKey: string): void {
    const idx = this.selectedExportColumns.indexOf(columnKey);
    if (idx === -1) {
      this.selectedExportColumns = [...this.selectedExportColumns, columnKey];
    } else {
      this.selectedExportColumns = this.selectedExportColumns.filter(c => c !== columnKey);
    }
  }

  isExportColumnSelected(columnKey: string): boolean {
    return this.selectedExportColumns.includes(columnKey);
  }

  selectAllExportColumns(): void {
    this.selectedExportColumns = [...this.availableExportColumns];
  }

  deselectAllExportColumns(): void {
    this.selectedExportColumns = [];
  }

  exportToExcel(): void {
    if (this.selectedExportColumns.length === 0) {
      this.exportError = 'Selecione pelo menos uma coluna para exportar.';
      return;
    }
    this.exportError = null;
    this.isExporting = true;
    this.vehiclesService.exportVehiclesToExcel(this.selectedExportColumns).subscribe({
      next: (blob) => {
        this.downloadExcelFile(blob);
        this.isExporting = false;
        this.closeExportModal();
      },
      error: () => {
        this.isExporting = false;
      }
    });
  }

  private downloadExcelFile(blob: Blob): void {
    try {
      let excelBlob = blob;
      if (!blob.type || !blob.type.includes('spreadsheet')) {
        excelBlob = new Blob([blob], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
      }
      const url = window.URL.createObjectURL(excelBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `exportacao_veiculos_${new Date().toISOString().split('T')[0]}.xlsx`;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
      this.errorService.showSuccessMessage('Exportação Excel gerada e baixada com sucesso!');
    } catch (error) {
      this.exportError = 'Erro ao fazer download do arquivo Excel.';
    }
  }
}
